import { useEffect, useState, useCallback, useRef } from "react";
import DOMPurify from "dompurify";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PenLine, Loader2, Mail, ImagePlus, Eye, Type, X } from "lucide-react";

const escapeHtml = (t) =>
  (t || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const nl2br = (t) => escapeHtml(t).replace(/\n/g, "<br>");

// Firma già esistente in HTML → testo semplice (retrocompatibilità).
const htmlToText = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = DOMPurify.sanitize(
    html.replace(/<br\s*\/?>(?![^<]*<\/)/gi, "\n").replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|tr)>/gi, "\n")
  );
  return (tmp.textContent || tmp.innerText || "").replace(/\n{3,}/g, "\n\n").trim();
};

const extractLogo = (html) => {
  if (!html) return "";
  const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return m ? m[1] : "";
};

// Costruisce l'HTML finale della firma dal testo semplice + logo.
const buildSignatureHtml = (text, logoUrl) => {
  const body = (text || "").trim();
  const parts = [];
  if (body) parts.push(`<div style="font-size:14px;color:#111;line-height:1.5">${nl2br(body)}</div>`);
  if (logoUrl) parts.push(`<div style="margin-top:8px"><img src="${logoUrl}" alt="logo" style="max-height:60px" /></div>`);
  return parts.join("");
};

export const SignaturePerMailbox = () => {
  const [accounts, setAccounts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [logos, setLogos] = useState({});
  const [busy, setBusy] = useState("");
  const [uploading, setUploading] = useState("");
  const [loading, setLoading] = useState(true);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/accounts");
      const accs = r.data || [];
      setAccounts(accs);
      setDrafts(Object.fromEntries(accs.map((a) => [a.id, a.signature_text ?? htmlToText(a.signature_html || "")])));
      setLogos(Object.fromEntries(accs.map((a) => [a.id, a.signature_logo_url ?? extractLogo(a.signature_html || "")])));
    } catch (err) { console.error("Caricamento caselle fallito:", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isDirty = (a) => {
    const curText = a.signature_text ?? htmlToText(a.signature_html || "");
    const curLogo = a.signature_logo_url ?? extractLogo(a.signature_html || "");
    return (drafts[a.id] ?? "") !== curText || (logos[a.id] ?? "") !== curLogo;
  };

  const save = async (a) => {
    setBusy(a.id);
    const text = drafts[a.id] || "";
    const logo = logos[a.id] || "";
    const signature_html = buildSignatureHtml(text, logo);
    try {
      await api.put(`/accounts/${a.id}/signature`, {
        signature_html,
        signature_text: text,
        signature_logo_url: logo,
      });
      setAccounts((prev) => prev.map((x) => x.id === a.id
        ? { ...x, signature_html, signature_text: text, signature_logo_url: logo } : x));
      toast.success("Firma della casella salvata");
    } catch (err) { console.error(err); toast.error("Salvataggio non riuscito"); }
    finally { setBusy(""); }
  };

  const uploadLogo = async (a, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(a.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/accounts/${a.id}/signature-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const absUrl = `${API.replace(/\/api$/, "")}${r.data.url}`;
      setLogos((d) => ({ ...d, [a.id]: absUrl }));
      toast.success("Logo caricato: verrà mostrato in fondo alla firma");
    } catch (err) { console.error(err); toast.error(err?.response?.data?.detail || "Caricamento logo non riuscito"); }
    finally { setUploading(""); }
  };

  const removeLogo = (a) => setLogos((d) => ({ ...d, [a.id]: "" }));

  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="signature-per-mailbox">
      <div className="flex items-center gap-2 mb-1">
        <PenLine className="h-4 w-4 text-[#007BFF]" />
        <h3 className="font-head text-lg font-semibold">Firma per casella</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Scrivi la firma come un normale testo: gli a-capo che inserisci vengono mantenuti nelle email.
        Puoi aggiungere un logo che apparirà in fondo alla firma. Quando è impostata, la firma viene inserita
        automaticamente in fondo alle risposte generate dall'AI e a tutte le email inviate da quella casella,
        e l'AI non firma più da sola. Se lasci vuoto, resta il comportamento attuale.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Caricamento caselle…</div>
      ) : accounts.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="signature-no-accounts">
          Nessuna casella collegata. Vai su “Connetti Account” per aggiungerne una.
        </div>
      ) : (
        <div className="space-y-4">
          {accounts.map((a) => (
            <div key={a.id} className="border border-border rounded-md p-4" data-testid={`signature-account-${a.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate">{a.email_address}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground mb-1"><Type className="h-3 w-3" />Testo della firma</div>
                  <textarea
                    data-testid={`signature-textarea-${a.id}`}
                    value={drafts[a.id] ?? ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                    placeholder={"Es.\nCordiali saluti,\nStudio Amministrazioni Rossi\nTel. 045 1234567"}
                    rows={7}
                    className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#007BFF] resize-y"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground mb-1"><Eye className="h-3 w-3" />Anteprima</div>
                  <div
                    data-testid={`signature-preview-${a.id}`}
                    className="w-full min-h-[168px] bg-background border border-border rounded-md px-3 py-2 text-sm overflow-auto"
                  >
                    {(drafts[a.id] || "").trim() || logos[a.id] ? (
                      <>
                        {(drafts[a.id] || "").trim() && (
                          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{drafts[a.id]}</div>
                        )}
                        {logos[a.id] && (
                          <img src={logos[a.id]} alt="logo" style={{ maxHeight: 60, marginTop: 8 }} />
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">L'anteprima della firma comparirà qui.</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <input ref={(el) => (fileInputs.current[a.id] = el)} type="file" accept="image/*" className="hidden"
                    data-testid={`signature-logo-input-${a.id}`} onChange={(e) => uploadLogo(a, e)} />
                  <Button variant="outline" size="sm" className="h-8 text-xs rounded-md"
                    data-testid={`signature-logo-btn-${a.id}`} disabled={uploading === a.id}
                    onClick={() => fileInputs.current[a.id]?.click()}>
                    {uploading === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ImagePlus className="h-3.5 w-3.5 mr-1" />}
                    {logos[a.id] ? "Cambia logo" : "Carica logo"}
                  </Button>
                  {logos[a.id] && (
                    <Button variant="ghost" size="sm" className="h-8 text-xs rounded-md text-muted-foreground hover:text-destructive"
                      data-testid={`signature-logo-remove-${a.id}`} onClick={() => removeLogo(a)}>
                      <X className="h-3.5 w-3.5 mr-1" />Rimuovi logo
                    </Button>
                  )}
                </div>
                <Button
                  data-testid={`signature-save-${a.id}`}
                  onClick={() => save(a)}
                  disabled={busy === a.id || !isDirty(a)}
                  className="h-8 bg-[#007BFF] hover:bg-[#0069D9] rounded-md text-xs">
                  {busy === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salva firma"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
